import { IconButton, TextField } from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import Close from '@material-ui/icons/Close';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Entity, EntityClassProperty } from '../../../../../types';
import { JupiterTheme } from '../../../../theme/types';
import {
  entityClassFromSchema,
  isExcludedProperty,
} from '../../../../util/properties';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    formSection: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    input: {
      marginBottom: theme.spacing(1.5),
      marginRight: theme.spacing(1.5),
      fontWeight: 'normal',
      flex: 1,
    },
    inputSection: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      justifyContent: 'space-between',
    },
    container: {
      width: '100%',
    },
  })
);

interface Props {
  entity: Entity;
  onUpdateProperties: (key: string, value: any) => void;
  tagsForm?: boolean;
}

interface State {
  customProperties: EntityClassProperty[];
  newPropertyName: string;
  newPropertyValue: string;
}

const tagsPrefix = 'tag.';

function isTag(propertyName: string) {
  return propertyName.indexOf(tagsPrefix) === 0;
}
export const useCustomPropertiesForm = (props: Props) => {
  const [state, setState] = useState<State>({
    customProperties: [],
    newPropertyName: '',
    newPropertyValue: '',
  });
  const { tagsForm, entity } = props;
  const { customProperties } = state;

  useEffect(() => {
    if (!props.entity.entityClass?.[0]) return;
    if (tagsForm) {
      fetchInitialTags(entity);
    } else {
      fetchInitialCustomProperties();
    }
  }, [props.entity.entityClass?.[0]]);

  const fetchInitialCustomProperties = () => {
    const model = props.entity.entityClass?.[0]
      ? entityClassFromSchema(props.entity.entityClass[0])
      : undefined;
    const customPropNames = Object.keys(props.entity.properties).filter(
      (name: string) => {
        if (isExcludedProperty(name)) {
          return false;
        }
        const prop = model?.properties.find((p: EntityClassProperty) => {
          return p.name === name;
        });

        return prop === undefined && !isTag(name);
      }
    );
    return addInitialProperties(customPropNames);
  };

  const fetchInitialTags = (entity: Entity) => {
    const tagNames = Object.keys(entity.properties).filter(isTag);
    addInitialProperties(tagNames);
  };

  const addInitialProperties = (propertyNames: string[]) => {
    if (propertyNames.length === customProperties.length) return;

    const tags = propertyNames.map((name: string) => {
      const newTag: EntityClassProperty = {
        name: name,
        description: '',
        type: 'string',
        required: false,
      };
      return newTag;
    });

    setState({ ...state, customProperties: tags });
  };

  const handleAddProperty = () => {
    const { onUpdateProperties } = props;
    const { newPropertyName, newPropertyValue } = state;
    if (newPropertyName && newPropertyValue) {
      const { customProperties } = state;
      const newProp: EntityClassProperty = {
        name: newPropertyName,
        description: '',
        type: 'string',
        required: false,
      };
      (customProperties as any).push(newProp);
      setState({
        ...state,
        customProperties,
        newPropertyName: '',
        newPropertyValue: '',
      });

      onUpdateProperties(newPropertyName, newPropertyValue);
    }
  };

  const handleUpdateNewValue = (e: ChangeEvent<HTMLInputElement>) => {
    const { onUpdateProperties } = props;
    const { newPropertyName } = state;
    setState({ ...state, newPropertyValue: e.target.value });
    if (newPropertyName) {
      onUpdateProperties(newPropertyName, e.target.value);
    }
  };

  const handleUpdateNewName = (e: ChangeEvent<HTMLInputElement>) => {
    const { onUpdateProperties, tagsForm } = props;
    const previousName = state.newPropertyName;
    if (previousName) {
      onUpdateProperties(previousName, null);
    }
    const newPropertyName =
      tagsForm && !e.target.value.startsWith(tagsPrefix)
        ? tagsPrefix + e.target.value
        : e.target.value;
    setState({ ...state, newPropertyName });
    onUpdateProperties(newPropertyName, state.newPropertyValue || '');
  };

  const handleDeleteProperty = (index: number) => {
    return () => {
      const { customProperties } = state;
      const { onUpdateProperties } = props;
      const prop: EntityClassProperty = customProperties[index];

      onUpdateProperties(prop.name, null);
      customProperties.splice(index, 1);
      setState({ ...state, customProperties });
    };
  };

  const handleUpdateExistingValue = (index: number) => {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const { customProperties } = state;
      const { onUpdateProperties } = props;
      const prop: EntityClassProperty = customProperties[index];

      onUpdateProperties(prop.name, event.target.value);
    };
  };

  const handleUpdateExistingName = (index: number) => {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const { customProperties }: any = state;
      const { entity, onUpdateProperties, tagsForm } = props;
      const prop: EntityClassProperty = customProperties[index];
      const existingValue = entity.properties[prop.name];
      const newName = tagsForm
        ? tagsPrefix + event.target.value
        : event.target.value;

      onUpdateProperties(prop.name, null);
      customProperties[index].name = newName;
      onUpdateProperties(newName, existingValue);

      setState({ ...state, customProperties });
    };
  };
  const { newPropertyName, newPropertyValue } = state;

  const displayName =
    tagsForm && newPropertyName.startsWith(tagsPrefix)
      ? newPropertyName?.replace(tagsPrefix, '')
      : newPropertyName;
  return {
    displayName,
    customProperties: state.customProperties,
    tagsForm,
    newPropertyValue,
    handleUpdateExistingName,
    handleUpdateExistingValue,
    handleUpdateNewValue,
    handleDeleteProperty,
    handleAddProperty,
    handleUpdateNewName,
  };
};
const CustomPropertiesForm = (props: Props) => {
  const classes = useStyles();
  const {
    displayName,
    customProperties,
    tagsForm,
    newPropertyValue,
    handleUpdateExistingName,
    handleUpdateExistingValue,
    handleUpdateNewValue,
    handleDeleteProperty,
    handleAddProperty,
    handleUpdateNewName,
  } = useCustomPropertiesForm(props);

  const getFields = () => {
    const { entity, tagsForm } = props;
    return customProperties.map((_p: EntityClassProperty, i: number) => {
      const propertyName = customProperties[i].name;
      const displayName =
        tagsForm && propertyName.startsWith(tagsPrefix)
          ? propertyName?.replace(tagsPrefix, '')
          : propertyName;
      return (
        <div className={classes.inputSection} key={i}>
          <TextField
            value={displayName}
            onChange={handleUpdateExistingName(i)}
            name={displayName + '-label'}
            label={displayName + '-label'}
            className={classes.input}
          />
          <TextField
            value={entity.properties[propertyName]}
            onChange={handleUpdateExistingValue(i)}
            name={displayName + '-value'}
            label={displayName + '-value'}
            className={classes.input}
          />
          <div>
            <IconButton onClick={handleDeleteProperty(i)}>
              <Close />
            </IconButton>
          </div>
        </div>
      );
    });
  };

  return (
    <div className={classes.container}>
      <div className={classes.inputSection}>
        <TextField
          value={displayName}
          onChange={handleUpdateNewName}
          name="property-name"
          label={(tagsForm ? 'Tag' : 'Property') + ' Name'}
          className={classes.input}
        />
        <TextField
          value={newPropertyValue}
          onChange={handleUpdateNewValue}
          name="property-value"
          label={(tagsForm ? 'Tag' : 'Property') + ' Value'}
          className={classes.input}
        />
        <div>
          <IconButton onClick={handleAddProperty}>
            <Add />
          </IconButton>
        </div>
      </div>
      <div>{getFields()}</div>
    </div>
  );
};

export default CustomPropertiesForm;
